// Review queue for car photo submissions (/api/admin/cars/submissions).
import { useCallback, useEffect, useState } from 'react';
import { getCarSubmissions, reviewCarSubmission, type AdminCarSubmission } from '../api/client';
import { AuthedImage, CarForm, FilterPills, Modal, button, formFrom, formToBody, statusBadge, type CarFormValues } from './CarsShared';

type StatusFilter = 'pending' | 'ai_approved' | 'duplicate' | 'approved' | 'rejected' | 'all';

function SubmissionPanel({ sub, onClose, onDone }: { sub: AdminCarSubmission; onClose: () => void; onDone: (s: AdminCarSubmission) => void }) {
  const analysis = (sub.aiAnalysis?.analysis ?? null) as Record<string, unknown> | null;
  const [form, setForm] = useState<CarFormValues>(() => formFrom(sub.car, analysis, sub));
  const [rejectReason, setRejectReason] = useState(sub.rejectReason ?? '');
  const [saving, setSaving] = useState(false);
  // Only the submission that created an item edits it; a duplicate shouldn't overwrite someone else's item.
  const editsItem = !sub.car || sub.isSourceOfCar;

  const review = async (status: string) => {
    if (status === 'rejected' && !confirm('Reject this submission?')) return;
    if (editsItem && status !== 'rejected' && !form.name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      const res = await reviewCarSubmission(sub.id, {
        status,
        ...(status === 'rejected' ? { rejectReason: rejectReason || undefined } : {}),
        ...(editsItem && status !== 'rejected' ? { car: formToBody(form) } : {}),
      });
      onDone(res.data.submission);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Car submission"
      onClose={onClose}
      footer={<>
        <button disabled={saving} className={button.approve} onClick={() => review('approved')}>Approve</button>
        <button disabled={saving} className={button.ai} onClick={() => review('ai_approved')}>AI Approved</button>
        <button disabled={saving} className={button.pending} onClick={() => review('pending')}>Set Pending</button>
        <input className="flex-1 min-w-[140px] px-2 py-1 text-sm border border-gray-300 rounded" placeholder="Reject reason (shown to submitter)" value={rejectReason} onChange={e => setRejectReason(e.target.value)} maxLength={200} />
        <button disabled={saving} className={button.reject} onClick={() => review('rejected')}>Reject</button>
      </>}
    >
      <div className="flex gap-4 flex-wrap">
        <div>
          <div className="text-xs text-gray-500 mb-1">Submitted photo</div>
          <AuthedImage src={sub.imageUrl} alt="Submission" className="w-56 h-56 object-contain rounded border" />
        </div>
        {sub.car && !sub.isSourceOfCar && (
          <div>
            <div className="text-xs text-gray-500 mb-1">Matches existing item ({sub.car.moderationStatus})</div>
            <AuthedImage src={sub.car.imageUrl} alt={sub.car.name} className="w-56 h-56 object-contain rounded border" />
            <div className="text-sm font-medium mt-1">{sub.car.name}</div>
          </div>
        )}
        <div className="flex-1 min-w-[200px] text-sm space-y-1">
          <div><span className={`px-1.5 py-0.5 text-xs font-medium rounded ${statusBadge(sub.status)}`}>{sub.status}</span>
            {sub.car && <span className={`ml-2 px-1.5 py-0.5 text-xs font-medium rounded ${statusBadge(sub.car.moderationStatus)}`}>item: {sub.car.moderationStatus}</span>}</div>
          <div className="text-gray-500">Submitted {new Date(sub.createdAt).toLocaleString()}</div>
          {sub.barcode && <div>Barcode <code className="text-xs">{sub.barcode}</code></div>}
          {sub.toyNumber && <div>Toy number <code className="text-xs">{sub.toyNumber}</code></div>}
          {sub.name && <div>Submitter's name: {sub.name}</div>}
          {sub.notes && <div>Notes: {sub.notes}</div>}
          <div className="text-xs text-gray-400">User {sub.userId ?? '(deleted)'}</div>
        </div>
      </div>

      <div className="bg-purple-50 rounded-lg p-3 border border-purple-200 text-sm">
        <div className="font-semibold text-purple-800 mb-1">
          AI review: {sub.ai?.validationResult ?? 'none'}{sub.ai?.confidence != null && ` · ${sub.ai.confidence}%`}
        </div>
        {sub.aiAnalysis?.error && <div className="text-red-600">AI error: {sub.aiAnalysis.error}</div>}
        {sub.ai?.reasoning && <div className="text-gray-700">{sub.ai.reasoning}</div>}
        <div className="flex gap-3 mt-1 text-xs">
          {sub.ai?.isCar === false && <span className="text-red-700">Not a car</span>}
          {sub.ai?.containsPeople && <span className="text-amber-700">⚠ Person visible: replace the photo before approving</span>}
          {analysis?.inappropriate === true && <span className="text-red-700">⚠ Flagged inappropriate</span>}
        </div>
      </div>

      {editsItem ? (
        <div>
          <div className="text-xs font-semibold text-gray-600 uppercase mb-2">{sub.car ? 'Catalog item' : 'Catalog item (created when you approve)'}</div>
          <CarForm value={form} onChange={setForm} />
        </div>
      ) : (
        <div className="text-sm text-gray-600">This photo matched an existing item by toy number. Approving approves that item; rejecting only rejects this submission. Edit the item on the Car Catalog page.</div>
      )}
    </Modal>
  );
}

export default function CarSubmissions() {
  const [items, setItems] = useState<AdminCarSubmission[]>([]);
  const [status, setStatus] = useState<StatusFilter>('pending');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<AdminCarSubmission | null>(null);
  const limit = 25;

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async (p: number) => {
    setLoading(true);
    setError('');
    try {
      const res = await getCarSubmissions(p, limit, status === 'all' ? undefined : status, debounced || undefined);
      setItems(prev => (p === 1 ? res.data.submissions : [...prev, ...res.data.submissions]));
      setTotal(res.data.pagination.total);
      setPage(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [status, debounced]);

  useEffect(() => { load(1); }, [load]);

  const onDone = (updated: AdminCarSubmission) => {
    setSelected(null);
    // Leave the row in place with its new status; Refresh re-applies the filter.
    setItems(prev => prev.map(s => (s.id === updated.id ? updated : s)));
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-bold text-slate-800">Car Submissions</h1>
          <span className="text-sm text-gray-500">{total.toLocaleString()} {status === 'all' ? 'total' : status}</span>
        </div>
        <button onClick={() => load(1)} className="px-3 py-1.5 bg-blue-500 text-white text-sm font-medium rounded hover:bg-blue-600">Refresh</button>
      </div>
      <div className="flex gap-3 items-center mb-3 flex-wrap">
        <input className="flex-1 max-w-md px-3 py-1.5 text-sm border border-gray-300 rounded" placeholder="Search name, barcode, toy number, notes…" value={search} onChange={e => setSearch(e.target.value)} />
        <FilterPills<StatusFilter>
          value={status}
          onChange={setStatus}
          options={[
            { value: 'pending', label: 'Pending' },
            { value: 'ai_approved', label: 'AI Approved' },
            { value: 'duplicate', label: 'Duplicates' },
            { value: 'approved', label: 'Approved' },
            { value: 'rejected', label: 'Rejected' },
            { value: 'all', label: 'All' },
          ]}
        />
      </div>
      {error && <div className="bg-red-50 text-red-600 px-3 py-2 rounded text-sm mb-2">{error}</div>}

      {!loading && items.length === 0 ? (
        <div className="bg-white rounded shadow-sm p-8 text-center">
          <div className="text-3xl mb-2">{status === 'pending' ? '🎉' : '📭'}</div>
          <div className="text-lg font-medium text-slate-800">{status === 'pending' ? 'Queue empty' : 'No submissions'}</div>
        </div>
      ) : (
        <div className="bg-white rounded shadow-sm divide-y divide-gray-100">
          {items.map(sub => (
            <div key={sub.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm" onClick={() => setSelected(sub)}>
              <AuthedImage src={sub.imageUrl} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800 truncate">{sub.car?.name ?? sub.name ?? (sub.aiAnalysis?.analysis?.name as string | undefined) ?? 'Untitled'}</div>
                <div className="text-xs text-gray-500 truncate">
                  {sub.barcode && <code className="mr-2">{sub.barcode}</code>}
                  {sub.toyNumber && <code className="mr-2">{sub.toyNumber}</code>}
                  {sub.notes}
                </div>
              </div>
              <div className="w-40 text-xs text-gray-600 flex-shrink-0">
                {sub.ai ? `AI ${sub.ai.validationResult}${sub.ai.confidence != null ? ` · ${sub.ai.confidence}%` : ''}` : 'no AI'}
                {sub.ai?.containsPeople && <span className="ml-1 text-amber-600" title="Person visible">⚠</span>}
              </div>
              <div className="w-24 text-xs text-gray-500 flex-shrink-0">{new Date(sub.createdAt).toLocaleDateString()}</div>
              <span className={`w-24 text-center px-1.5 py-0.5 text-xs font-medium rounded flex-shrink-0 ${statusBadge(sub.status)}`}>{sub.status}</span>
            </div>
          ))}
        </div>
      )}
      {items.length < total && (
        <div className="text-center mt-3">
          <button disabled={loading} onClick={() => load(page + 1)} className={button.plain}>{loading ? 'Loading…' : 'Load more'}</button>
        </div>
      )}
      {selected && <SubmissionPanel sub={selected} onClose={() => setSelected(null)} onDone={onDone} />}
    </div>
  );
}
